/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { i18n } from 'config/locales';
import { extractDate } from 'components/Section/History/dateranges';
import * as formTypes from 'constants/formTypes';
import { CitizenshipMultipleItemModel } from 'forms/sections/citizenship/multiple/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonRequired, CommonOnError
} from 'types/props';
import { DateRangeModel } from 'forms/shared/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Country from 'components/Form/Country';
import Show from 'components/Form/Show';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';
import BranchCollection from 'components/Form/BranchCollection';
import CitizenshipForeignPassportItem from './CitizenshipForeignPassportItem';

interface Props extends Partial<CitizenshipMultipleItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType: formTypes.FormTypes
  formVersion: string
  errors?: []

  requireMultipleCitizenshipRenounced?: boolean
}

export default class CitizenshipItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  static defaultProps: Partial<Props> = {
    errors: [],
    onUpdate: () => { },
  };

  update = (queue) => {
    this.props.onUpdate({
      ForeignPassports: this.props.ForeignPassports,
      Country: this.props.Country,
      Dates: this.props.Dates,
      Current: this.props.Current,
      CurrentExplanation: this.props.CurrentExplanation,
      How: this.props.How,
      Renounced: this.props.Renounced,
      RenouncedExplanation: this.props.RenouncedExplanation,
      Comments: this.props.Comments,
      CurrentComments: this.props.CurrentComments,
      EverIssuedPassport: this.props.EverIssuedPassport,
      IsPassportActive: this.props.IsPassportActive,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateForeignPassports = (values: CitizenshipMultipleItemModel['ForeignPassports']) => {
    this.update({
      ForeignPassports: values,
    });
  };

  updateCountry = (values: CitizenshipMultipleItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateDates = (values: CitizenshipMultipleItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateHow = (values: CitizenshipMultipleItemModel['How']) => {
    this.update({
      How: values,
    });
  };

  updateRenounced = (values: CitizenshipMultipleItemModel['Renounced']) => {
    this.update({
      Renounced: values,
    });
  };

  updateRenouncedExplanation = (values: CitizenshipMultipleItemModel['RenouncedExplanation']) => {
    this.update({
      RenouncedExplanation: values,
    });
  };

  updateCurrent = (values: CitizenshipMultipleItemModel['Current']) => {
    this.update({
      Current: values,
    });
  };

  updateCurrentExplanation = (values: CitizenshipMultipleItemModel['CurrentExplanation']) => {
    this.update({
      CurrentExplanation: values,
    });
  };

  updateCurrentComments = (values: CitizenshipMultipleItemModel['CurrentComments']) => {
    this.update({
      CurrentComments: values,
    });
  };

  updateComments = (values: CitizenshipMultipleItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateEverIssuedPassport = (values: CitizenshipMultipleItemModel['EverIssuedPassport']) => {
    this.update({
      EverIssuedPassport: values,
      IsPassportActive: values?.value === 'Yes' ? this.props.IsPassportActive : {},
    });
  };

  updateIsPassportActive = (values: CitizenshipMultipleItemModel['IsPassportActive']) => {
    this.update({
      IsPassportActive: values,
    });
  };

  render() {
    const {
      requireMultipleCitizenshipRenounced, formType, formVersion, errors
    } = this.props;
    const d: DateRangeModel = this.props.Dates || {};
    const to = extractDate(d.to);
    const from = extractDate(d.from);
    const validationAlt = [formTypes.SF85].includes(formType || '');
    const presentIndicator = validationAlt ? d.present : !d.present;
    const showCurrentQuestion = to && from && presentIndicator;

    const country = this.props.Country?.value || [];
    const showNonUSQuestions = !country.includes('United States');
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(formType);
    const isPvqAB = (isPVQ && formType?.includes('B'));
    const accordionErrors = errors && errors.filter((e) => e.indexOf('ForeignPassportList.accordion') === 0);

    return isPVQ ? (
      <div className="citizenship-item">
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            attribute={`${attribute}.Country`}
            className="citizenship-country"
            updateCountryFocused={this.updateCountryFocused}
            onUpdate={this.updateCountry}
            onError={this.props.onError}
            required={this.props.required}

          />
        </Field>

        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.how')}
          attribute={`${attribute}.How`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="How"
            attribute={`${attribute}.How`}
            {...this.props.How}
            className="citizenship-how"
            onUpdate={this.updateHow}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.period')}
          titleSize="h4"
          optional
          className="period-pvq no-margin-bottom"
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            {...this.props.Dates}
            className="citizenship-dates-pvq"
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          name="EverIssuedPassport"
          attribute={`${attribute}.EverIssuedPassport`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.passport')}
          labelSize="h4"
          className="citizenship-current no-margin-bottom"
          {...this.props.EverIssuedPassport}
          onUpdate={this.updateEverIssuedPassport}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.EverIssuedPassport?.value === 'Yes' && (
          <Branch
            name="IsPassportActive"
            attribute={`${attribute}.IsPassportActive`}
            label={i18n.t('citizenship.pvq.additional.heading.citizenship.active')}
            labelSize="h4"
            className="citizenship-current-pvq no-margin-bottom"
            {...this.props.IsPassportActive}
            onUpdate={this.updateIsPassportActive}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {isPvqAB
          && (
            <BranchCollection
              {...this.props.ForeignPassports}
              dnkLabel={i18n.t('citizenship.pvq.additional.collection.citizenship.dnkLabel')}
              label={i18n.t('citizenship.pvq.additional.heading.citizenship.foreignPassport')}
              labelSize="h4"
              appendLabel={i18n.t('citizenship.pvq.additional.collection.citizenship.foreignAppendTitle')}
              className="foreign-passports"
              onUpdate={this.updateForeignPassports}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ForeignPassports`}
            >
              <CitizenshipForeignPassportItem
                name="Item"
                bind
                onError={this.props.onError}
                formType={this.props.formType}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ForeignPassports`}
              />
            </BranchCollection>
          )}
      </div>
    ) : (
      <div className="citizenship-item">
        <Field
          title={i18n.t('citizenship.multiple.heading.citizenship.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            attribute={`${attribute}.Country`}
            className="citizenship-country"
            updateCountryFocused={this.updateCountryFocused}
            onUpdate={this.updateCountry}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Show when={formType !== formTypes.SF852013}>
          <Field
            title={i18n.t('citizenship.multiple.heading.citizenship.period')}
            help="citizenship.multiple.help.citizenship.dates"
            titleSize="h4"
            optional
            className="period no-margin-bottom"
          />

          <Field
            title={i18n.t('citizenship.multiple.heading.citizenship.dates')}
            attribute={`${attribute}.Dates`}
            titleSize="label"
            adjustFor="daterange"
            scrollIntoView={this.props.scrollIntoView}
          >
            <DateRange
              name="Dates"
              attribute={`${attribute}.Dates`}
              {...this.props.Dates}
              className="citizenship-dates"
              minDateEqualTo
              onUpdate={this.updateDates}
              onError={this.props.onError}
              required={this.props.required}
              hideFromDay
              hideToDay
            />
          </Field>

          <Show when={showNonUSQuestions}>
            <Field
              title={i18n.t('citizenship.multiple.heading.citizenship.how')}
              attribute={`${attribute}.How`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="How"
                attribute={`${attribute}.How`}
                {...this.props.How}
                className="citizenship-how"
                onUpdate={this.updateHow}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Show when={requireMultipleCitizenshipRenounced}>
              <Branch
                name="Renounced"
                exactAttribute
                attribute={`${attribute}.Renounced`}
                label={i18n.t('citizenship.multiple.heading.citizenship.renounced')}
                labelSize="h4"
                className="citizenship-renounced no-margin-bottom"
                {...this.props.Renounced}
                onUpdate={this.updateRenounced}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />

              <Field
                title={i18n.t('citizenship.multiple.heading.citizenship.renouncedexplanation')}
                attribute={`${attribute}.RenouncedExplanation`}
                titleSize="label"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  name="RenouncedExplanation"
                  attribute={`${attribute}.RenouncedExplanation`}
                  {...this.props.RenouncedExplanation}
                  className="citizenship-renounced-explanation"
                  onUpdate={this.updateRenouncedExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </Show>
          </Show>
          {!isFormSF852013(formType, formVersion) && (
            <Comments
              title={i18n.t('citizenship.comments.optional')}
              name={`${attribute}Comments`}
              className="optional-comment"
              attribute={`${attribute}Comments`}
              {...this.props.Comments}
              onUpdate={this.updateComments}
              required={false}
            />
          )}
        </Show>
        <Show when={showCurrentQuestion}>
          <div>
            <Branch
              name="Current"
              attribute={`${attribute}.Current`}
              label={i18n.t('citizenship.multiple.heading.citizenship.current')}
              labelSize="h4"
              className="citizenship-current no-margin-bottom"
              {...this.props.Current}
              onUpdate={this.updateCurrent}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />

            <Field
              title={i18n.t('citizenship.multiple.heading.citizenship.currentexplanation')}
              attribute={`${attribute}.CurrentExplanation`}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="CurrentExplanation"
                attribute={`${attribute}.CurrentExplanation`}
                {...this.props.CurrentExplanation}
                className="citizenship-current-explanation"
                onUpdate={this.updateCurrentExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            {!isFormSF852013(formType, formVersion) && (
              <Comments
                title={i18n.t('citizenship.comments.optional')}
                name={`${attribute}CurrentComments`}
                className="optional-comment"
                attribute={`${attribute}CurrentComments`}
                {...this.props.CurrentComments}
                onUpdate={this.updateCurrentComments}
                required={false}
              />
            )}
          </div>
        </Show>
      </div>
    );
  }
}

CitizenshipItem.defaultProps = {
  requireMultipleCitizenshipRenounced: true,
  Country: {},
  Dates: {},
  How: {},
  Renounced: {},
  RenouncedExplanation: {},
  Current: {},
  CurrentExplanation: {},
  CurrentComments: {},
  CountryFocused: false,
  EverIssuedPassport: '',
  IsPassportActive: '',
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
};
